import { useCallback, useState, type FormEvent } from 'react';
import type { SdtWindow, SdtWindowInput } from '@detect/shared';
import { sdtService } from '../../services/alerting';
import { resourceService } from '../../services/resources';
import { clientService } from '../../services/clients';
import type { WithId } from '../../services/types';
import { useTenant } from '../../contexts/TenantContext';
import { useCursorList } from '../../hooks/useCursorList';
import { usePickerOptions } from '../../hooks/usePickerOptions';
import {
  LoadingSpinner,
  ErrorState,
  EmptyState,
  Modal,
  LoadMoreButton,
} from '../../components/common';
import { formatDateTime } from '../../utils/formatters';

/**
 * Scheduled downtime (PRD §2.5). Windows are immutable once cut
 * (create/delete only). The UI creates ONE-TIME windows; recurring
 * windows are supported by the API and land in the UI later.
 */
export function SdtTab() {
  const { tenantId, hasRole } = useTenant();
  const [creating, setCreating] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);

  const fetchPage = useCallback(
    (tid: string, startAfter: string | undefined) =>
      sdtService.list(tid, { limit: 50, startAfter }),
    []
  );
  const list = useCursorList<WithId<SdtWindow>>(fetchPage, 'all');

  const remove = async (window_: WithId<SdtWindow>) => {
    if (!tenantId) return;
    if (!window.confirm('Delete this downtime window?')) return;
    setActionError(null);
    try {
      await sdtService.remove(tenantId, window_.id);
      void list.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  return (
    <div>
      <div className="page-actions tab-actions">
        {hasRole('agent') && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setCreating(true)}
          >
            Schedule downtime
          </button>
        )}
      </div>
      {actionError !== null && <ErrorState error={actionError} />}
      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.reload()} />
      ) : list.loading ? (
        <LoadingSpinner />
      ) : list.items.length === 0 ? (
        <EmptyState
          title="No downtime windows"
          description="Schedule downtime to suppress alerts during maintenance."
        />
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Scope</th>
                <th>Target</th>
                <th>Window</th>
                <th>Comment</th>
                {hasRole('agent') && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {list.items.map((w) => (
                <tr key={w.id}>
                  <td>{w.scopeType}</td>
                  <td>{w.targetId ?? '—'}</td>
                  <td>
                    {w.startsAt && w.endsAt
                      ? `${formatDateTime(w.startsAt)} → ${formatDateTime(w.endsAt)}`
                      : describeRecurrence(w.recurrence)}
                  </td>
                  <td>
                    {w.comment ?? '—'}
                    {w.changeWindowId && (
                      <div className="text-muted">Part of a change window</div>
                    )}
                  </td>
                  {hasRole('agent') && (
                    <td className="row-actions">
                      {w.changeWindowId ? (
                        <span className="text-muted">Manage in Change windows</span>
                      ) : (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => void remove(w)}
                      >
                        Delete
                      </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoadMoreButton
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        loadMoreError={list.loadMoreError}
        onLoadMore={() => void list.loadMore()}
      />
      {creating && (
        <SdtModal
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void list.reload();
          }}
        />
      )}
    </div>
  );
}

function toOffsetIso(local: string): string {
  // datetime-local gives naive local time; the API requires an offset.
  return new Date(local).toISOString();
}


const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function describeRecurrence(rec: SdtWindow['recurrence']): string {
  if (!rec) return 'recurring';
  const window_ = `${rec.startTime}–${rec.endTime} (${rec.timezone})`;
  switch (rec.frequency) {
    case 'daily':
      return `Daily ${window_}`;
    case 'weekly':
      return `Weekly ${(rec.daysOfWeek ?? [])
        .map((d) => DAY_LABELS[d])
        .join('/')} ${window_}`;
    case 'monthly':
      return `Monthly day ${rec.dayOfMonth} ${window_}`;
  }
}

function SdtModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { tenantId, clientScope } = useTenant();
  const [scopeType, setScopeType] = useState<'tenant' | 'client' | 'resource'>(
    'resource'
  );
  const [targetId, setTargetId] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [comment, setComment] = useState('');
  /** Repeat cadence — 'once' keeps the one-time startsAt/endsAt shape;
   *  the rest build the recurrence block (backend supported since M5;
   *  this editor closes gap-audit issue #8). */
  const [frequency, setFrequency] = useState<
    'once' | 'daily' | 'weekly' | 'monthly'
  >('once');
  const [daysOfWeek, setDaysOfWeek] = useState<ReadonlySet<number>>(new Set());
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [startTime, setStartTime] = useState('22:00');
  const [endTime, setEndTime] = useState('23:00');
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const resourcesQuery = usePickerOptions(['resources'], (tid, startAfter) =>
    resourceService.list(tid, { limit: 100, startAfter })
  );
  const clientsQuery = usePickerOptions(['clients'], (tid, startAfter) =>
    clientService.list(tid, { limit: 100, startAfter })
  );
  const clients = (clientsQuery.data?.items ?? []).filter(
    (c) => clientScope === null || clientScope.includes(c.id)
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    if (frequency === 'weekly' && daysOfWeek.size === 0) {
      setError(new Error('Pick at least one day of the week.'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const timezone =
        Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const input: SdtWindowInput = {
        scopeType,
        ...(scopeType !== 'tenant' ? { targetId } : {}),
        ...(frequency === 'once'
          ? { startsAt: toOffsetIso(startsAt), endsAt: toOffsetIso(endsAt) }
          : {
              recurrence: {
                frequency,
                startTime,
                endTime,
                timezone,
                ...(frequency === 'weekly'
                  ? { daysOfWeek: [...daysOfWeek].sort((a, b) => a - b) }
                  : {}),
                ...(frequency === 'monthly' ? { dayOfMonth } : {}),
              },
            }),
        ...(comment ? { comment } : {}),
      };
      await sdtService.create(tenantId, input);
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Schedule downtime" onClose={onClose}>
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <div className="form-field">
          <label htmlFor="sdt-scope">Scope</label>
          <select
            id="sdt-scope"
            name="scopeType"
            value={scopeType}
            onChange={(e) => {
              setScopeType(e.target.value as 'tenant' | 'client' | 'resource');
              setTargetId('');
            }}
          >
            <option value="resource">Resource</option>
            <option value="client">Client</option>
            <option value="tenant">Whole tenant</option>
          </select>
        </div>
        {scopeType === 'resource' && (
          <div className="form-field">
            <label htmlFor="sdt-resource">Resource</label>
            <select
              id="sdt-resource"
              name="targetId"
              required
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
            >
              <option value="">— select —</option>
              {(resourcesQuery.data?.items ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.displayName ?? r.name}
                </option>
              ))}
            </select>
            {resourcesQuery.data?.truncated && (
              <p className="text-muted">Resource list truncated.</p>
            )}
          </div>
        )}
        {scopeType === 'client' && (
          <div className="form-field">
            <label htmlFor="sdt-client">Client</label>
            <select
              id="sdt-client"
              name="targetId"
              required
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
            >
              <option value="">— select —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="form-field">
          <label htmlFor="sdt-frequency">Repeats</label>
          <select
            id="sdt-frequency"
            name="frequency"
            value={frequency}
            onChange={(e) =>
              setFrequency(
                e.target.value as 'once' | 'daily' | 'weekly' | 'monthly'
              )
            }
          >
            <option value="once">One-time</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        {frequency === 'once' ? (
          <>
            <div className="form-field">
              <label htmlFor="sdt-start">Starts</label>
              <input
                id="sdt-start"
                name="startsAt"
                type="datetime-local"
                required
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="sdt-end">Ends</label>
              <input
                id="sdt-end"
                name="endsAt"
                type="datetime-local"
                required
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
              />
            </div>
          </>
        ) : (
          <>
            {frequency === 'weekly' && (
              <fieldset className="form-field">
                <legend>On days</legend>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                  (label, day) => (
                    <label key={day} className="checkbox-label checkbox-inline">
                      <input
                        type="checkbox"
                        checked={daysOfWeek.has(day)}
                        onChange={(e) =>
                          setDaysOfWeek((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(day);
                            else next.delete(day);
                            return next;
                          })
                        }
                      />
                      {label}
                    </label>
                  )
                )}
              </fieldset>
            )}
            {frequency === 'monthly' && (
              <div className="form-field">
                <label htmlFor="sdt-dom">Day of month</label>
                <input
                  id="sdt-dom"
                  name="dayOfMonth"
                  type="number"
                  min={1}
                  max={31}
                  required
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(Number(e.target.value))}
                />
              </div>
            )}
            <div className="form-field">
              <label htmlFor="sdt-time-start">From (local time)</label>
              <input
                id="sdt-time-start"
                name="startTime"
                type="time"
                required
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="sdt-time-end">Until</label>
              <input
                id="sdt-time-end"
                name="endTime"
                type="time"
                required
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </>
        )}
        <div className="form-field">
          <label htmlFor="sdt-comment">Comment</label>
          <input
            id="sdt-comment"
            name="comment"
            placeholder="Why is this window scheduled?"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Scheduling…' : 'Schedule'}
        </button>
      </form>
    </Modal>
  );
}
